class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.clear.bind(this));
    }
    if (this.input) {
      const debouncedChange = typeof FoxTheme !== 'undefined' && FoxTheme.utils && FoxTheme.utils.debounce
        ? FoxTheme.utils.debounce(this.onChange.bind(this), 300)
        : this.onChange.bind(this);
      this.input.addEventListener('input', debouncedChange);
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.routes || !FoxTheme.utils) return;
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.removeAttribute('results');
  }

  setupURL() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.routes || !FoxTheme.routes.predictive_search_url || !FoxTheme.utils || !FoxTheme.utils.getSectionId) {
      return new URL(window.shopUrl || window.location.origin);
    }
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  onChange() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.routes || !FoxTheme.utils) return;
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    if (this.resetButton) this.resetButton.classList.add('btn--loading');

    const searchForm = this;
    fetch(url, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    })
      .then((response) => {
        // 417 = Unsupported buyer locale (e.g. Arabic not in Shopify predictive search supported languages)
        if (response.status === 417) {
          const q = searchForm.getQuery();
          if (q) {
            const base = window.shopUrl || window.location.origin;
            window.location.href = base + '/search?q=' + encodeURIComponent(q);
          }
          throw new Error('Predictive search not available for this language');
        }
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (error.message !== 'Predictive search not available for this language') {
          console.error('Error fetching data: ', error);
          this.setAttribute('error', 'Failed to load data');
        }
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        if (this.resetButton) this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    if (typeof FoxTheme === 'undefined' || !FoxTheme.utils || !FoxTheme.utils.getSectionId) return;
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
